'use client';
import { TEXT_INPUT_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { forwardRef } from 'react';

export enum TextBorderType {
  Round = 'round',
  Square = 'square',
}

const CONTAINER_CLASSES = 'relative flex w-full flex-col';

const getInputClasses = (isSquare: boolean, isSearchIcon?: boolean, hasCharCounter?: boolean, hasError?: boolean) =>
  cn(
    'h-[46px] w-full border border-solid py-0 text-base leading-sm no-underline placeholder:text-gray-600 focus:ml-[-3px] focus:w-[calc(100%+6px)] focus:outline-none',
    isSquare ? 'rounded-md' : 'rounded-[23px]',
    isSearchIcon ? 'pl-[43px]' : 'pl-20',
    hasCharCounter ? 'pr-[54px]' : 'pr-40',
    hasError
      ? 'border-red-600'
      : 'border-gray-400 hover:border-gray-600 active:border-gray-600 focus:border-4 focus:border-gray-800/30',
  );

const TextInput = forwardRef<
  HTMLInputElement,
  {
    placeholder?: string;
    onChange: (searchText: string) => void;
    textValue: string;
    inputTestId?: string;
    maxLength?: number;
    charCounter?: string;
    textBorderType?: TextBorderType;
    isSearchIcon?: boolean;
    errorMessage?: string;
    hasClearBtn?: boolean;
  }
>(function TextInput(
  {
    placeholder,
    onChange,
    textValue,
    inputTestId,
    maxLength,
    charCounter,
    textBorderType = TextBorderType.Round,
    isSearchIcon,
    errorMessage,
    hasClearBtn = true,
  },
  ref,
) {
  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className={CONTAINER_CLASSES}>
      {isSearchIcon && (
        <i
          className="susu-icons absolute left-[15px] top-[13px] h-20 w-20 text-xl leading-none text-gray-800"
          translate="no"
        >
          search
        </i>
      )}
      <input
        ref={ref}
        className={getInputClasses(
          textBorderType === TextBorderType.Square,
          isSearchIcon,
          !!charCounter,
          !!errorMessage,
        )}
        data-testid={inputTestId}
        type="text"
        placeholder={placeholder}
        onChange={onTextChange}
        value={textValue}
        maxLength={maxLength}
      />
      {hasClearBtn && !charCounter && textValue.length > 0 && (
        <div
          className="absolute right-1 top-1 flex h-44 w-44 cursor-pointer items-center justify-center"
          onClick={() => {
            onChange('');
          }}
          onKeyDown={e => {
            onEnter(e, () => {
              onChange('');
            });
          }}
        >
          <div
            className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-gray-500 text-white [&>i]:pt-0 [&>i]:text-sm"
            data-testid={TEXT_INPUT_TEST_IDS.CLEAR_INPUT_BTN}
          >
            <i className="susu-icons" translate="no">
              close
            </i>
          </div>
        </div>
      )}
      {charCounter && (
        <span className="absolute right-14 top-[13px] flex items-center justify-center text-sm font-light leading-sm text-gray-600">
          {charCounter}
        </span>
      )}
      {errorMessage && (
        <div data-testid={`${inputTestId}-error`} className="pt-8 text-xs font-normal leading-xs text-red-600">
          {errorMessage}
        </div>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;
